// Per-warehouse picker for the Picking module's stock-allocation strategy.
// Mirrors StockAllocationMethod's pill-segmented control so the admin
// settings page reads consistently — the only differences are the
// settings field name (`picking_alloc_method`) and the i18n keys.
//
// Note: this is a separate setting from the Locations-page
// `stock_allocation_method` because Picking has its own scoring formula
// (proximity + temperature weights) and may diverge over time.
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import { apiFetch } from "../../api/apiFetch";

type Method = "fifo" | "fastest";

const METHODS: { value: Method; labelKey: string }[] = [
    { value: "fifo",    labelKey: "picking_alloc_fifo" },
    { value: "fastest", labelKey: "picking_alloc_fastest" },
];

const PickingAllocationMethod = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [rows, setRows] = useState<{ warehouse: string; picking_alloc_method: Method }[]>([]);
    const [savingFor, setSavingFor] = useState<string | null>(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && Array.isArray(data)) {
                    setRows(data.map((r: { warehouse: string; picking_alloc_method?: string }) => ({
                        warehouse: r.warehouse,
                        picking_alloc_method: (r.picking_alloc_method === "fifo" ? "fifo" : "fastest") as Method,
                    })));
                }
            } catch { /* leave empty */ }
        })();
        return () => { alive = false; };
    }, []);

    const save = async (warehouse: string, method: Method) => {
        if (readOnly) return;
        setSavingFor(warehouse);
        const prev = rows;
        setRows(curr => curr.map(r => r.warehouse === warehouse ? { ...r, picking_alloc_method: method } : r));
        try {
            const res = await apiFetch(`/settings/${encodeURIComponent(warehouse)}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ picking_alloc_method: method }),
            });
            if (!res.ok) throw new Error("save");
        } catch {
            setRows(prev);
            toast.error(t("server_error"), { toastId: `picking-alloc-method-err-${warehouse}` });
        } finally {
            setSavingFor(null);
        }
    };

    if (rows.length === 0) {
        return <div className="admin-settings-empty">{t("no_warehouses")}</div>;
    }

    return (
        <div className="stock-alloc-list">
            {rows.map(r => {
                const isSaving = savingFor === r.warehouse;
                return (
                    <div key={r.warehouse} className="stock-alloc-row">
                        <div className="stock-alloc-warehouse">{r.warehouse}</div>
                        <div
                            className={`stock-alloc-segment${isSaving ? " stock-alloc-segment--saving" : ""}`}
                            role="radiogroup"
                            aria-label={t("picking_alloc_method")}
                        >
                            {METHODS.map(opt => {
                                const active = r.picking_alloc_method === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        role="radio"
                                        aria-checked={active}
                                        disabled={isSaving || active || readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                        className={`stock-alloc-pill${active ? " stock-alloc-pill--active" : ""}`}
                                        onClick={() => save(r.warehouse, opt.value)}
                                    >
                                        {t(opt.labelKey)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default PickingAllocationMethod;
